'use strict'

import {
  toFiniteNumber
} from './portal-geo-utils.js'
import {
  parseKmlCoordTriplet
} from './portal-kml-utils.js'
import {
  PORTAL_PRESETS,
  resolvePortalPresetDefaultValue
} from './portal-presets.js'
import {
  getMissionAltitudeContextFor3d,
  normalizeAltitudeMode,
  syncPointsAltitudeFields
} from './portal-altitude-utils.js'
import {
  xmlToObj,
  updatePointXmlByPath,
  updateXmlByPath
} from './portal-point-map-utils.js'
import {
  setByPath
} from './portal-waypoint-core-utils.js'
import {
  findFirstChildByTagSuffix,
  getDirectChildrenByLocalName,
  copyXmlLeafValuesBySkeleton,
  createWpmlElement,
  parsePlacemarkCoordinates,
  setPlacemarkCoordinates
} from './portal-xml-utils.js'
import {
  normalizeKmzActionGroupEntry,
  normalizeKmzActionEntry
} from './portal-kmz-action-utils.js'
import {
  getPointName
} from './portal-import-state-utils.js'
import {
  buildKmzScopedHeadingPropsFromSources,
  applyKmzScopedHeadingProps,
  syncKmzPointHeadingScopesFromXml,
  resolveKmzTemplateExportUseGlobalHeading,
  resolveKmzTemplateExportHeadingField,
  resolveKmzWaylinesExportUseGlobalHeading,
  resolveKmzWaylinesExportHeadingField
} from './portal-kmz-heading-utils.js'
import {
  buildKmzScopedSpeedPropsFromSources,
  applyKmzScopedSpeedProps
} from './portal-kmz-speed-utils.js'
import {
  buildKmzScopedGimbalPropsFromSources,
  applyKmzScopedGimbalProps,
  syncKmzPointGimbalScopesFromXml,
  resolveKmzTemplateExportGimbalTemplatePitch,
  resolveKmzWaylinesExportGimbalField
} from './portal-kmz-gimbal-utils.js'
import {
  KMZ_PAYLOAD_CHILDREN,
  buildKmzScopedPayloadPropsFromSources,
  applyKmzScopedPayloadProps,
  syncKmzMissionPayloadScopesFromXml,
  resolveKmzTemplateExportPayloadField
} from './portal-kmz-payload-utils.js'
import {
  buildKmzScopedTurnPropsFromSources,
  applyKmzScopedTurnProps,
  syncKmzPointTurnScopesFromXml,
  resolveKmzTemplateExportUseGlobalTurnParam,
  resolveKmzTemplateExportTurnField,
  resolveKmzTemplateExportGlobalTurnMode,
  resolveKmzWaylinesExportTurnField,
  resolveKmzWaylinesExportGlobalTurnMode
} from './portal-kmz-turn-utils.js'
import {
  buildKmzScopedStraightLinePropsFromSources,
  applyKmzScopedStraightLineProps,
  syncKmzPointStraightLineScopesFromXml,
  resolveKmzTemplateExportUseStraightLine,
  resolveKmzTemplateExportGlobalUseStraightLine,
  resolveKmzWaylinesExportUseStraightLine
} from './portal-kmz-straight-line-utils.js'
import {
  buildKmzScopedAltitudePropsFromSources,
  applyKmzScopedAltitudeProps,
  syncKmzMissionAltitudeScopesFromXml,
  resolveKmzTemplateExportGlobalHeight,
  resolveKmzTemplateExportHeightMode,
  resolveKmzTemplateExportUseGlobalHeight,
  resolveKmzWaylinesExportExecuteHeightMode,
  resolveKmzWaylinesExportUseGlobalHeight
} from './portal-kmz-altitude-utils.js'

const _ = window._
const kmzActionGroupAllocatorStateMap = new WeakMap()
const KMZ_FILE_SUFFIX_POINT_NAME_TOKEN = '<pointName>'
const KMZ_FILE_SUFFIX_FALLBACK_PREFIX = 'MapNOTAM_'
const KMZ_EXPORT_SCOPE_TEMPLATE = 'template'
const KMZ_EXPORT_SCOPE_WAYLINES = 'waylines'
const KMZ_INTERNAL_WAYLINES_ACTION_GROUP_EXTRAS_KEY = '__kmzWaylinesExecutionExtras'
const KMZ_INTERNAL_WAYLINES_ACTION_GROUP_OVERRIDES_KEY = '__kmzWaylinesExecutionOverrides'
const KMZ_INTERNAL_TEMPLATE_ACTION_GROUPS_BASELINE_KEY = '__kmzTemplateActionGroupsBaseline'
const KMZ_POINT_HEIGHT_PATH_ALIASES = [
  'Point.height',
  'wpml:height',
  'height',
  'template.wpml:height',
  'waylines.wpml:height'
]
const KMZ_POINT_EXECUTE_HEIGHT_PATH_ALIASES = [
  'Point.executeHeight',
  'wpml:executeHeight',
  'executeHeight',
  'template.wpml:executeHeight',
  'waylines.wpml:executeHeight'
]
const KMZ_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES = [
  'wpml:ellipsoidHeight',
  'ellipsoidHeight',
  'template.wpml:ellipsoidHeight',
  'waylines.wpml:ellipsoidHeight'
]
const KMZ_TEMPLATE_HEIGHT_MODE_FALLBACK = 'EGM96'
const KMZ_EXECUTE_HEIGHT_MODE_FALLBACK = 'WGS84'
const KMZ_WAYPOINT_HEADING_PARAM_CHILD_ORDER = [
  'waypointHeadingMode',
  'waypointHeadingAngle',
  'waypointPoiPoint',
  'waypointHeadingAngleEnable',
  'waypointHeadingPathMode',
  'waypointHeadingPoiIndex'
]
const KMZ_WAYPOINT_TURN_PARAM_CHILD_ORDER = [
  'waypointTurnMode',
  'waypointTurnDampingDist'
]

const fnBuildKmzAltitudeSyncContext = function (importMissionState) {
  return {
    importMissionState: importMissionState || null,
    DEM_PROVIDER_PRIORITY: [],
    ALTITUDE_RULES: PORTAL_PRESETS.altitudeRules,
    POINT_HEIGHT_PATH_ALIASES: KMZ_POINT_HEIGHT_PATH_ALIASES,
    POINT_EXECUTE_HEIGHT_PATH_ALIASES: KMZ_POINT_EXECUTE_HEIGHT_PATH_ALIASES,
    POINT_ELLIPSOID_HEIGHT_PATH_ALIASES: KMZ_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES,
    NON_EXPORT_POINT_HEIGHT_PATH: 'project.height',
    fnIsDemProviderEnabled: function () { return true },
    fnSetByPath: setByPath,
    fnUpdatePointXmlByPath: function (point, path, value) {
      return updatePointXmlByPath(point, path, value, function (xmlNode, xmlPath, xmlValue) {
        return updateXmlByPath(xmlNode, xmlPath, xmlValue, function () {})
      })
    },
    fnTemplateSupportsPointCoordinatesField: function () { return false }
  }
}

const fnGetNormalizedModeSet = function (presets, altitudeCtx) {
  const set = new Set()
  ;(Array.isArray(presets) ? presets : []).forEach((entry) => {
    const rawValue = String((entry && entry.value !== undefined) ? entry.value : entry || '').trim()
    if (!rawValue) return
    set.add(normalizeAltitudeMode(rawValue, altitudeCtx))
  })
  return set
}

const fnFindXmlLeafBySuffixPath = function (rootNode, suffixPath) {
  const suffixes = Array.isArray(suffixPath) ? suffixPath : []
  let current = rootNode || null
  for (let i = 0; i < suffixes.length; i += 1) {
    if (!current) return null
    current = findFirstChildByTagSuffix(current, suffixes[i])
  }
  return current || null
}

const fnSetXmlLeafBySuffixPath = function (rootNode, suffixPath, value) {
  const leaf = fnFindXmlLeafBySuffixPath(rootNode, suffixPath)
  if (!leaf) return false
  const nextText = String(value === undefined || value === null ? '' : value)
  if (String(leaf.textContent || '').trim() === nextText.trim()) return false
  leaf.textContent = nextText
  return true
}

const fnResolveTemplateHeightModeForExport = function (rawMode, altitudeCtx) {
  const normalized = normalizeAltitudeMode(rawMode, altitudeCtx)
  const allowedModes = fnGetNormalizedModeSet(PORTAL_PRESETS.options.templateHeightModePresets, altitudeCtx)
  if (allowedModes.has(normalized)) return normalized
  if (normalized === 'WGS84') return 'EGM96'
  return KMZ_TEMPLATE_HEIGHT_MODE_FALLBACK
}

const fnResolveExecuteHeightModeForExport = function (rawMode, templateMode, altitudeCtx) {
  const normalized = normalizeAltitudeMode(rawMode, altitudeCtx)
  const normalizedTemplateMode = normalizeAltitudeMode(templateMode, altitudeCtx)
  const allowedModes = fnGetNormalizedModeSet(PORTAL_PRESETS.options.executeHeightModePresets, altitudeCtx)
  if (allowedModes.has(normalized)) return normalized
  if (allowedModes.has(normalizedTemplateMode)) return normalizedTemplateMode
  return KMZ_EXECUTE_HEIGHT_MODE_FALLBACK
}

const fnGetFirstFinitePointValue = function (point, paths) {
  const props = (point && point.props && typeof point.props === 'object') ? point.props : {}
  const list = Array.isArray(paths) ? paths : []
  for (let i = 0; i < list.length; i += 1) {
    const value = toFiniteNumber(_.get(props, list[i]))
    if (value !== null) return value
  }
  return null
}

const fnResolvePointAltitudeFallback = function (point) {
  const props = (point && point.props && typeof point.props === 'object') ? point.props : {}
  const directCandidates = [
    point && point.altitude,
    _.get(props, 'coordinates.alt'),
    _.get(props, 'Point.coordinates.alt')
  ]
  for (let i = 0; i < directCandidates.length; i += 1) {
    const value = toFiniteNumber(directCandidates[i])
    if (value !== null) return value
  }
  return null
}

const fnNormalizeKmzActionParamsForExport = function (actuator, params) {
  void actuator
  const out = {}
  const src = (params && typeof params === 'object') ? params : {}
  Object.keys(src).forEach((key) => {
    const safeKey = String(key || '').trim()
    if (!safeKey) return
    const value = src[key]
    if (value === undefined || value === null) return
    out[safeKey] = String(value)
  })
  const payloadLensIndex = String(out.payloadLensIndex || '').trim()
  if (!payloadLensIndex && Object.prototype.hasOwnProperty.call(out, 'payloadLensIndex')) {
    delete out.payloadLensIndex
  }
  if (Object.prototype.hasOwnProperty.call(out, 'useGlobalPayloadLensIndex')) {
    out.useGlobalPayloadLensIndex = payloadLensIndex ? String(out.useGlobalPayloadLensIndex || '0').trim() || '0' : '1'
  }
  return out
}

const fnEnsurePlacemarkAltitudeField = function (placemarkEl, suffixPath, value) {
  const leaf = fnFindXmlLeafBySuffixPath(placemarkEl, suffixPath)
  const numericValue = toFiniteNumber(value)
  if (!leaf || numericValue === null) return false
  if (String(leaf.textContent || '').trim()) return false
  leaf.textContent = String(numericValue)
  return true
}

const fnNormalizeHeadingExportState = function (point, fnResolveHeadingField, fnResolveUseGlobalHeading, fnToBoolLike) {
  const headingAngle = toFiniteNumber(fnResolveHeadingField(point, 'waypointHeadingAngle', null))
  const headingModeRaw = String(fnResolveHeadingField(point, 'waypointHeadingMode', '') || '').trim()
  const headingPathModeRaw = String(fnResolveHeadingField(point, 'waypointHeadingPathMode', '') || '').trim()
  return {
    headingMode: headingModeRaw.toLowerCase(),
    headingPathModeRaw: headingPathModeRaw,
    hasNonZeroHeadingAngle: headingAngle !== null && Math.abs(headingAngle) > 1e-9,
    useGlobalHeading: fnToBoolLike(fnResolveUseGlobalHeading(point, null))
  }
}

const fnNormalizeKmzHeadingForExportNode = function (xmlNode, headingState) {
  if (!xmlNode || !headingState) return false
  let changed = false
  const fnSetHeadingPath = function (path, value) {
    if (!updateXmlByPath(xmlNode, path, value, function () {})) return
    changed = true
  }
  const headingMode = String(headingState.headingMode || '').trim().toLowerCase()
  const headingPathModeRaw = String(headingState.headingPathModeRaw || '').trim()
  if (headingState.useGlobalHeading === true) {
    fnSetHeadingPath(
      'wpml:waypointHeadingParam.wpml:waypointHeadingAngleEnable',
      headingMode === 'smoothtransition' ? 1 : 0
    )
    if (headingMode === 'smoothtransition' && !headingPathModeRaw) {
      fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingPathMode', 'followBadArc')
    }
    return changed
  }
  if (!headingMode || headingMode === 'manually') {
    if (headingState.hasNonZeroHeadingAngle) {
      fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingMode', 'smoothTransition')
      fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingAngleEnable', 1)
      if (!headingPathModeRaw) {
        fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingPathMode', 'followBadArc')
      }
      return changed
    }
    fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingMode', 'followWayline')
    fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingAngleEnable', 0)
    if (!headingPathModeRaw) {
      fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingPathMode', 'clockwise')
    }
    return changed
  }
  if (headingMode === 'followwayline') {
    fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingAngleEnable', 0)
    if (!headingPathModeRaw) {
      fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingPathMode', 'clockwise')
    }
    return changed
  }
  if (headingMode === 'smoothtransition') {
    fnSetHeadingPath(
      'wpml:waypointHeadingParam.wpml:waypointHeadingAngleEnable',
      headingState.hasNonZeroHeadingAngle ? 1 : 0
    )
    if (!headingPathModeRaw) {
      fnSetHeadingPath('wpml:waypointHeadingParam.wpml:waypointHeadingPathMode', 'followBadArc')
    }
  }
  return changed
}

const fnResolvePointHeadingParamValue = function (point, sourceTemplatePm, sourceWaylinesPm, childName) {
  void sourceTemplatePm
  void sourceWaylinesPm
  const child = String(childName || '').trim()
  if (!child) return ''
  const templateValue = resolveKmzTemplateExportHeadingField(
    point,
    child,
    resolvePortalPresetDefaultValue('wpml:waypointHeadingParam.wpml:' + child)
  )
  const waylinesValue = resolveKmzWaylinesExportHeadingField(
    point,
    child,
    templateValue
  )
  const templateText = String(templateValue === undefined || templateValue === null ? '' : templateValue).trim()
  const waylinesText = String(waylinesValue === undefined || waylinesValue === null ? '' : waylinesValue).trim()
  if (waylinesText && waylinesText !== templateText) {
    return waylinesText
  }
  return templateText
}

const fnEnsureTemplateWaypointHeadingParam = function (targetTemplatePm, sourceTemplatePm, sourceWaylinesPm, point) {
  if (!targetTemplatePm || !targetTemplatePm.ownerDocument) return
  const wpmlNs = fnGetWpmlNamespaceUriFromNode(targetTemplatePm)
  const existingBlocks = getDirectChildrenByLocalName(targetTemplatePm, 'waypointHeadingParam')
  const headingBlock = existingBlocks[0] || createWpmlElement(targetTemplatePm.ownerDocument, 'waypointHeadingParam', wpmlNs)
  if (!headingBlock) return
  existingBlocks.slice(1).forEach((node) => {
    if (node.parentNode === targetTemplatePm) targetTemplatePm.removeChild(node)
  })
  while (headingBlock.firstChild) {
    headingBlock.removeChild(headingBlock.firstChild)
  }
  KMZ_WAYPOINT_HEADING_PARAM_CHILD_ORDER.forEach((childName) => {
    const childEl = createWpmlElement(targetTemplatePm.ownerDocument, childName, wpmlNs)
    if (!childEl) return
    childEl.textContent = fnResolvePointHeadingParamValue(point, sourceTemplatePm, sourceWaylinesPm, childName)
    headingBlock.appendChild(childEl)
  })
  if (headingBlock.parentNode === targetTemplatePm) return
  const turnAnchor = findFirstChildByTagSuffix(targetTemplatePm, 'useGlobalTurnParam')
  const gimbalAnchor = findFirstChildByTagSuffix(targetTemplatePm, 'gimbalPitchAngle')
  const useStraightLineAnchor = findFirstChildByTagSuffix(targetTemplatePm, 'useStraightLine')
  const anchor = turnAnchor || gimbalAnchor || useStraightLineAnchor || null
  if (anchor && anchor.parentNode === targetTemplatePm) {
    targetTemplatePm.insertBefore(headingBlock, anchor)
  } else {
    targetTemplatePm.appendChild(headingBlock)
  }
}

const fnResolvePointTurnParamValue = function (point, sourceTemplatePm, sourceWaylinesPm, childName, scopeName) {
  void sourceTemplatePm
  void sourceWaylinesPm
  const child = String(childName || '').trim()
  const scope = String(scopeName || KMZ_EXPORT_SCOPE_TEMPLATE).trim().toLowerCase()
  if (!child) return ''
  const resolver = (scope === KMZ_EXPORT_SCOPE_WAYLINES)
    ? resolveKmzWaylinesExportTurnField
    : resolveKmzTemplateExportTurnField
  const value = resolver(
    point,
    child,
    resolvePortalPresetDefaultValue('wpml:waypointTurnParam.wpml:' + child)
  )
  return String(value === undefined || value === null ? '' : value).trim()
}

const fnEnsureTemplateWaypointTurnParam = function (targetTemplatePm, sourceTemplatePm, sourceWaylinesPm, point) {
  if (!targetTemplatePm || !targetTemplatePm.ownerDocument) return
  const wpmlNs = fnGetWpmlNamespaceUriFromNode(targetTemplatePm)
  const existingBlocks = getDirectChildrenByLocalName(targetTemplatePm, 'waypointTurnParam')
  const turnBlock = existingBlocks[0] || createWpmlElement(targetTemplatePm.ownerDocument, 'waypointTurnParam', wpmlNs)
  if (!turnBlock) return
  existingBlocks.slice(1).forEach((node) => {
    if (node.parentNode === targetTemplatePm) targetTemplatePm.removeChild(node)
  })
  while (turnBlock.firstChild) {
    turnBlock.removeChild(turnBlock.firstChild)
  }
  KMZ_WAYPOINT_TURN_PARAM_CHILD_ORDER.forEach((childName) => {
    const childEl = createWpmlElement(targetTemplatePm.ownerDocument, childName, wpmlNs)
    if (!childEl) return
    childEl.textContent = fnResolvePointTurnParamValue(point, sourceTemplatePm, sourceWaylinesPm, childName, KMZ_EXPORT_SCOPE_TEMPLATE)
    turnBlock.appendChild(childEl)
  })
  if (turnBlock.parentNode === targetTemplatePm) return
  const gimbalAnchor = findFirstChildByTagSuffix(targetTemplatePm, 'gimbalPitchAngle')
  const useStraightLineAnchor = findFirstChildByTagSuffix(targetTemplatePm, 'useStraightLine')
  const anchor = gimbalAnchor || useStraightLineAnchor || null
  if (anchor && anchor.parentNode === targetTemplatePm) {
    targetTemplatePm.insertBefore(turnBlock, anchor)
  } else {
    targetTemplatePm.appendChild(turnBlock)
  }
}

const fnNormalizeKmzTurnForExportNode = function (xmlNode, point, scopeName) {
  if (!xmlNode || !point) return false
  const scope = String(scopeName || KMZ_EXPORT_SCOPE_TEMPLATE).trim().toLowerCase()
  const resolveLocalValue = function (childName) {
    return fnResolvePointTurnParamValue(point, null, null, childName, scope)
  }
  let changed = false
  if (scope === KMZ_EXPORT_SCOPE_TEMPLATE) {
    const useGlobalValue = resolveKmzTemplateExportUseGlobalTurnParam(
      point,
      resolvePortalPresetDefaultValue('wpml:useGlobalTurnParam')
    )
    if (updateXmlByPath(xmlNode, 'wpml:useGlobalTurnParam', useGlobalValue, function () {})) {
      changed = true
    }
  }
  KMZ_WAYPOINT_TURN_PARAM_CHILD_ORDER.forEach((childName) => {
    const value = resolveLocalValue(childName)
    if (updateXmlByPath(xmlNode, 'wpml:waypointTurnParam.wpml:' + childName, value, function () {})) {
      changed = true
    }
  })
  return changed
}

const fnResolvePointUseStraightLineValue = function (point, scopeName) {
  const scope = String(scopeName || KMZ_EXPORT_SCOPE_TEMPLATE).trim().toLowerCase()
  const resolver = (scope === KMZ_EXPORT_SCOPE_WAYLINES)
    ? resolveKmzWaylinesExportUseStraightLine
    : resolveKmzTemplateExportUseStraightLine
  const value = resolver(
    point,
    resolvePortalPresetDefaultValue('wpml:useStraightLine')
  )
  return String(value === undefined || value === null ? '' : value).trim()
}

const fnNormalizeKmzStraightLineForExportNode = function (xmlNode, point, scopeName) {
  if (!xmlNode || !point) return false
  const value = fnResolvePointUseStraightLineValue(point, scopeName)
  return updateXmlByPath(xmlNode, 'wpml:useStraightLine', value, function () {})
}

const fnResolveCanonicalGimbalPitchValueForExport = function (point) {
  const templatePitch = resolveKmzTemplateExportGimbalTemplatePitch(point, undefined)
  const waylinesPitch = resolveKmzWaylinesExportGimbalField(point, 'waypointGimbalPitchAngle', undefined)
  const waylinesPitchText = String(waylinesPitch === undefined || waylinesPitch === null ? '' : waylinesPitch).trim()
  const templatePitchText = String(templatePitch === undefined || templatePitch === null ? '' : templatePitch).trim()
  if (waylinesPitchText) return waylinesPitchText
  if (templatePitchText) return templatePitchText
  return String(resolvePortalPresetDefaultValue('wpml:gimbalPitchAngle') || '').trim()
}

const fnResolveWaylinesGimbalYawValueForExport = function (point) {
  const fallback = resolvePortalPresetDefaultValue('wpml:waypointGimbalHeadingParam.wpml:waypointGimbalYawAngle')
  const yawValue = resolveKmzWaylinesExportGimbalField(point, 'waypointGimbalYawAngle', fallback)
  return String(yawValue === undefined || yawValue === null ? '' : yawValue).trim()
}

const fnNormalizeKmzGimbalForExportNodes = function (templateNode, waylinesNode, point) {
  if (!point) return false
  const canonicalPitch = fnResolveCanonicalGimbalPitchValueForExport(point)
  const waylinesYaw = fnResolveWaylinesGimbalYawValueForExport(point)
  let changed = false
  if (templateNode) {
    if (updateXmlByPath(templateNode, 'wpml:gimbalPitchAngle', canonicalPitch, function () {})) {
      changed = true
    }
  }
  if (waylinesNode) {
    if (updateXmlByPath(waylinesNode, 'wpml:waypointGimbalHeadingParam.wpml:waypointGimbalPitchAngle', canonicalPitch, function () {})) {
      changed = true
    }
    if (updateXmlByPath(waylinesNode, 'wpml:waypointGimbalHeadingParam.wpml:waypointGimbalYawAngle', waylinesYaw, function () {})) {
      changed = true
    }
  }
  return changed
}

const fnEnsurePlacemarkUseStraightLine = function (placemarkEl, point, scopeName) {
  if (!placemarkEl || !placemarkEl.ownerDocument || !point) return false
  const value = fnResolvePointUseStraightLineValue(point, scopeName)
  const existing = findFirstChildByTagSuffix(placemarkEl, 'useStraightLine')
  if (existing) {
    if (String(existing.textContent || '').trim() === value) return false
    existing.textContent = value
    return true
  }
  const wpmlNs = fnGetWpmlNamespaceUriFromNode(placemarkEl)
  const useStraightLineEl = createWpmlElement(placemarkEl.ownerDocument, 'useStraightLine', wpmlNs)
  if (!useStraightLineEl) return false
  useStraightLineEl.textContent = value
  const anchor = findFirstChildByTagSuffix(placemarkEl, 'waypointGimbalHeadingParam') ||
    findFirstChildByTagSuffix(placemarkEl, 'isRisky') ||
    findFirstChildByTagSuffix(placemarkEl, 'waypointWorkType') ||
    null
  if (anchor && anchor.parentNode === placemarkEl) {
    placemarkEl.insertBefore(useStraightLineEl, anchor)
  } else {
    placemarkEl.appendChild(useStraightLineEl)
  }
  return true
}

const fnEnsureExportAltitudeFields = function (targetTemplatePm, targetWaylinesPm, point) {
  const fallbackHeight = fnGetFirstFinitePointValue(point, KMZ_POINT_HEIGHT_PATH_ALIASES)
  const fallbackExecuteHeight = fnGetFirstFinitePointValue(point, KMZ_POINT_EXECUTE_HEIGHT_PATH_ALIASES)
  const fallbackEllipsoidHeight = fnGetFirstFinitePointValue(point, KMZ_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES)
  const fallbackAbsoluteAltitude = fnResolvePointAltitudeFallback(point)
  fnEnsurePlacemarkAltitudeField(
    targetTemplatePm,
    ['height'],
    fallbackHeight !== null ? fallbackHeight : (fallbackExecuteHeight !== null ? fallbackExecuteHeight : fallbackAbsoluteAltitude)
  )
  fnEnsurePlacemarkAltitudeField(
    targetWaylinesPm,
    ['executeHeight'],
    fallbackExecuteHeight !== null ? fallbackExecuteHeight : (fallbackHeight !== null ? fallbackHeight : fallbackAbsoluteAltitude)
  )
  fnEnsurePlacemarkAltitudeField(
    targetTemplatePm,
    ['ellipsoidHeight'],
    fallbackEllipsoidHeight !== null ? fallbackEllipsoidHeight : fallbackAbsoluteAltitude
  )
}

const fnExtractKmzActionGroupEntries = function (rawValue) {
  if (Array.isArray(rawValue)) return rawValue
  if (rawValue && typeof rawValue === 'object') {
    const numericKeys = Object.keys(rawValue).filter((key) => /^\d+$/.test(String(key)))
    if (numericKeys.length) {
      return numericKeys
        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
        .map((key) => rawValue[key])
    }
    return [rawValue]
  }
  return []
}

const fnNormalizeKmzActionGroups = function (rawValue, fallbackIndex) {
  const fallback = Number.isFinite(Number(fallbackIndex)) ? Number(fallbackIndex) : 0
  return fnExtractKmzActionGroupEntries(rawValue)
    .map((entry) => normalizeKmzActionGroupEntry(entry, fallback))
    .filter((entry) => !!entry)
}

const fnNormalizeKmzActionGroupOverrideMap = function (rawValue, fallbackIndex) {
  if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) return {}
  const out = {}
  Object.keys(rawValue).forEach((key) => {
    const normalized = normalizeKmzActionGroupEntry(rawValue[key], fallbackIndex)
    if (!normalized) return
    out[String(normalized.actionGroupId)] = normalized
  })
  return out
}

const fnBuildActionTriggerComparable = function (rawTrigger) {
  const trigger = (rawTrigger && typeof rawTrigger === 'object') ? rawTrigger : {}
  const type = String(trigger.actionTriggerType || '').trim()
  const params = {}
  Object.keys(trigger)
    .filter((key) => String(key || '').trim() && key !== 'actionTriggerType')
    .sort((a, b) => String(a).localeCompare(String(b)))
    .forEach((key) => {
      params[String(key)] = String(trigger[key] === undefined || trigger[key] === null ? '' : trigger[key])
    })
  return {
    actionTriggerType: type,
    actionTriggerParams: params
  }
}

const fnBuildActionParamsComparable = function (rawParams) {
  const params = (rawParams && typeof rawParams === 'object') ? rawParams : {}
  const out = {}
  Object.keys(params)
    .filter((key) => String(key || '').trim())
    .sort((a, b) => String(a).localeCompare(String(b)))
    .forEach((key) => {
      out[String(key)] = String(params[key] === undefined || params[key] === null ? '' : params[key])
    })
  return out
}

const fnBuildKmzActionGroupComparableShape = function (group) {
  const normalized = normalizeKmzActionGroupEntry(group, 0)
  if (!normalized) return null
  const actions = (Array.isArray(normalized.actions) ? normalized.actions : [])
    .map((entry) => normalizeKmzActionEntry(entry))
    .filter((entry) => !!entry)
    .map((entry) => {
      return {
        actuator: String(entry.actuator || '').trim(),
        params: fnBuildActionParamsComparable(entry.params)
      }
    })
  return {
    actionGroupId: String(normalized.actionGroupId || ''),
    actionGroupStartIndex: String(normalized.actionGroupStartIndex || ''),
    actionGroupEndIndex: String(normalized.actionGroupEndIndex || ''),
    actionGroupMode: String(normalized.actionGroupMode || ''),
    actionTrigger: fnBuildActionTriggerComparable(normalized.actionTrigger),
    actions: actions
  }
}

const fnAreKmzActionGroupsEquivalent = function (a, b) {
  const left = fnBuildKmzActionGroupComparableShape(a)
  const right = fnBuildKmzActionGroupComparableShape(b)
  return JSON.stringify(left) === JSON.stringify(right)
}

const fnSortActionGroupsById = function (groups) {
  const list = (Array.isArray(groups) ? groups : [])
    .map((group) => _.cloneDeep(group || {}))
    .filter((group) => !!group && group.actionGroupId !== undefined && group.actionGroupId !== null)
  return list.sort((a, b) => {
    const aRaw = String(a.actionGroupId || '').trim()
    const bRaw = String(b.actionGroupId || '').trim()
    const aNum = Number(aRaw)
    const bNum = Number(bRaw)
    const aNumOk = Number.isFinite(aNum)
    const bNumOk = Number.isFinite(bNum)
    if (aNumOk && bNumOk) return aNum - bNum
    if (aNumOk) return -1
    if (bNumOk) return 1
    return aRaw.localeCompare(bRaw)
  })
}

const fnSerializeActionGroupsForProps = function (groups) {
  const normalized = fnSortActionGroupsById(groups)
  if (!normalized.length) return null
  return normalized.length === 1 ? normalized[0] : normalized
}

const fnBuildActionGroupIdSet = function (groups) {
  const out = new Set()
  ;(Array.isArray(groups) ? groups : []).forEach((group) => {
    if (!group) return
    out.add(String(group.actionGroupId || ''))
  })
  return out
}

const fnBuildImportActionLayering = function (templateGroupsRaw, waylineGroupsRaw, options) {
  const opts = (options && typeof options === 'object') ? options : {}
  const promoteWaylineIfTemplateEmpty = !!opts.promoteWaylineIfTemplateEmpty
  const templateGroups = fnSortActionGroupsById(templateGroupsRaw)
  const waylineGroups = fnSortActionGroupsById(waylineGroupsRaw)
  if (promoteWaylineIfTemplateEmpty && !templateGroups.length && waylineGroups.length) {
    return {
      templateActionGroups: _.cloneDeep(waylineGroups),
      templateActionGroupsBaseline: _.cloneDeep(waylineGroups),
      waylinesExecutionExtras: [],
      waylinesExecutionOverrides: {}
    }
  }
  const templateById = {}
  templateGroups.forEach((group) => {
    templateById[String(group.actionGroupId)] = group
  })
  const extras = []
  const overrides = {}
  waylineGroups.forEach((group) => {
    const id = String(group.actionGroupId)
    const templateGroup = templateById[id]
    if (!templateGroup) {
      extras.push(_.cloneDeep(group))
      return
    }
    if (!fnAreKmzActionGroupsEquivalent(templateGroup, group)) {
      overrides[id] = _.cloneDeep(group)
    }
  })
  return {
    templateActionGroups: _.cloneDeep(templateGroups),
    templateActionGroupsBaseline: _.cloneDeep(templateGroups),
    waylinesExecutionExtras: fnSortActionGroupsById(extras),
    waylinesExecutionOverrides: overrides
  }
}

const fnGetNormalizedTemplateActionGroupsFromPoint = function (point, fallbackIndex) {
  const props = (point && point.props && typeof point.props === 'object') ? point.props : {}
  return fnNormalizeKmzActionGroups(_.get(props, 'wpml:actionGroup'), fallbackIndex)
}

const fnGetNormalizedTemplateActionGroupsBaselineFromPoint = function (point, fallbackIndex) {
  const props = (point && point.props && typeof point.props === 'object') ? point.props : {}
  return fnNormalizeKmzActionGroups(props[KMZ_INTERNAL_TEMPLATE_ACTION_GROUPS_BASELINE_KEY], fallbackIndex)
}

const fnGetNormalizedWaylinesActionGroupExtrasFromPoint = function (point, fallbackIndex) {
  const props = (point && point.props && typeof point.props === 'object') ? point.props : {}
  return fnNormalizeKmzActionGroups(props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_EXTRAS_KEY], fallbackIndex)
}

const fnGetNormalizedWaylinesActionGroupOverridesFromPoint = function (point, fallbackIndex) {
  const props = (point && point.props && typeof point.props === 'object') ? point.props : {}
  return fnNormalizeKmzActionGroupOverrideMap(props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_OVERRIDES_KEY], fallbackIndex)
}

const fnReserveActionGroupIdsFromWaylinesMeta = function (point, state) {
  if (!point || !state || !state.seen) return
  const extras = fnGetNormalizedWaylinesActionGroupExtrasFromPoint(point, point.idx)
  extras.forEach((group) => {
    const parsed = fnParseNonNegativeInt(group && group.actionGroupId)
    if (parsed === null) return
    state.seen.add(parsed)
  })
  const overrides = fnGetNormalizedWaylinesActionGroupOverridesFromPoint(point, point.idx)
  Object.keys(overrides).forEach((key) => {
    const parsed = fnParseNonNegativeInt(key)
    if (parsed === null) return
    state.seen.add(parsed)
  })
}

const fnResolveWaylinesActionGroupsForExport = function (point, fallbackIndex) {
  const templateGroups = fnGetNormalizedTemplateActionGroupsFromPoint(point, fallbackIndex)
  const templateBaseline = fnGetNormalizedTemplateActionGroupsBaselineFromPoint(point, fallbackIndex)
  const extras = fnGetNormalizedWaylinesActionGroupExtrasFromPoint(point, fallbackIndex)
  const overrides = fnGetNormalizedWaylinesActionGroupOverridesFromPoint(point, fallbackIndex)
  if (!templateGroups.length) {
    return fnSortActionGroupsById(extras)
  }
  const baselineById = {}
  templateBaseline.forEach((group) => {
    baselineById[String(group.actionGroupId)] = group
  })
  const waylinesGroups = templateGroups.map((group) => {
    const id = String(group.actionGroupId)
    const overrideGroup = overrides[id]
    if (!overrideGroup) return _.cloneDeep(group)
    const baselineGroup = baselineById[id]
    if (baselineGroup && fnAreKmzActionGroupsEquivalent(group, baselineGroup)) {
      return _.cloneDeep(overrideGroup)
    }
    return _.cloneDeep(group)
  })
  const waylineIds = fnBuildActionGroupIdSet(waylinesGroups)
  extras.forEach((group) => {
    const id = String(group.actionGroupId)
    if (waylineIds.has(id)) return
    waylineIds.add(id)
    waylinesGroups.push(_.cloneDeep(group))
  })
  return fnSortActionGroupsById(waylinesGroups)
}

const fnGetWpmlNamespaceUriFromNode = function (node) {
  if (!node || !node.lookupNamespaceURI) return 'http://www.dji.com/wpmz/1.0.6'
  return String(node.lookupNamespaceURI('wpml') || '').trim() || 'http://www.dji.com/wpmz/1.0.6'
}

const fnToScopeFlag = function (rawValue, fallbackValue) {
  if (rawValue === undefined || rawValue === null || rawValue === '') return !!fallbackValue
  if (typeof rawValue === 'boolean') return rawValue
  const text = String(rawValue).trim().toLowerCase()
  if (!text) return !!fallbackValue
  if (text === '1' || text === 'true' || text === 'yes' || text === 'on') return true
  if (text === '0' || text === 'false' || text === 'no' || text === 'off') return false
  const n = Number(text)
  if (Number.isFinite(n)) return n !== 0
  return !!fallbackValue
}

const fnResolveScopeAllowed = function (scopeRule, scopeName, fallbackScopeRule) {
  const fallbackRule = (fallbackScopeRule && typeof fallbackScopeRule === 'object') ? fallbackScopeRule : {}
  const fallbackAllowed = fnToScopeFlag(fallbackRule[scopeName], true)
  const rule = (scopeRule && typeof scopeRule === 'object') ? scopeRule : {}
  return fnToScopeFlag(rule[scopeName], fallbackAllowed)
}

const fnGetKmzActionExportTruthTable = function () {
  const table = _.get(PORTAL_PRESETS, 'actions.kmzExportTruthTable')
  if (!table || typeof table !== 'object') return {}
  return table
}

const fnFilterKmzActionByExportScope = function (action, scopeName, truthTable) {
  const normalizedAction = normalizeKmzActionEntry(action)
  if (!normalizedAction) return null
  const actionName = String(normalizedAction.actuator || '').trim()
  if (!actionName) return null
  const table = (truthTable && typeof truthTable === 'object') ? truthTable : {}
  const defaultScope = (table.scopeDefaults && typeof table.scopeDefaults === 'object')
    ? table.scopeDefaults
    : { template: 1, waylines: 1 }
  const unknownActionScope = (table.unknownActionScope && typeof table.unknownActionScope === 'object')
    ? table.unknownActionScope
    : defaultScope
  const unknownParamScope = (table.unknownParamScope && typeof table.unknownParamScope === 'object')
    ? table.unknownParamScope
    : defaultScope
  const actionScopeRule = _.get(table, ['actionScopes', actionName])
  const actionScopeKnown = !!(actionScopeRule && typeof actionScopeRule === 'object')
  const paramRuleMap = _.get(table, ['paramScopes', actionName])
  const srcParams = (normalizedAction.params && typeof normalizedAction.params === 'object') ? normalizedAction.params : {}
  const hasUnknownParamScope = Object.keys(srcParams).some((paramKey) => {
    const key = String(paramKey || '').trim()
    if (!key) return false
    const paramScopeRule = (paramRuleMap && typeof paramRuleMap === 'object') ? paramRuleMap[key] : null
    return !(paramScopeRule && typeof paramScopeRule === 'object')
  })
  if (!actionScopeKnown || hasUnknownParamScope) {
    return _.assign({}, normalizedAction, {
      params: fnNormalizeKmzActionParamsForExport(actionName, srcParams)
    })
  }
  const actionAllowed = fnResolveScopeAllowed(actionScopeRule, scopeName, unknownActionScope)
  if (!actionAllowed) return null

  const nextParams = {}
  Object.keys(srcParams).forEach((paramKey) => {
    const key = String(paramKey || '').trim()
    if (!key) return
    const paramScopeRule = (paramRuleMap && typeof paramRuleMap === 'object') ? paramRuleMap[key] : null
    const paramAllowed = fnResolveScopeAllowed(paramScopeRule, scopeName, unknownParamScope)
    if (!paramAllowed) return
    nextParams[key] = srcParams[paramKey]
  })

  return _.assign({}, normalizedAction, {
    params: fnNormalizeKmzActionParamsForExport(actionName, nextParams)
  })
}

const fnRemoveDirectChildrenByLocalName = function (parentNode, localName) {
  const nodes = getDirectChildrenByLocalName(parentNode, localName)
  nodes.forEach((node) => {
    if (node.parentNode === parentNode) parentNode.removeChild(node)
  })
}

const fnAppendWpmlTextChild = function (parentNode, doc, wpmlNs, tagName, value) {
  const child = createWpmlElement(doc, tagName, wpmlNs)
  if (!child) return null
  child.textContent = String(value === undefined || value === null ? '' : value)
  parentNode.appendChild(child)
  return child
}

const fnWriteActionGroupsToPlacemark = function (placemarkEl, groups, scopeName) {
  if (!placemarkEl || !placemarkEl.ownerDocument) return
  const doc = placemarkEl.ownerDocument
  const wpmlNs = fnGetWpmlNamespaceUriFromNode(placemarkEl)
  const scope = String(scopeName || '').trim().toLowerCase() === KMZ_EXPORT_SCOPE_TEMPLATE
    ? KMZ_EXPORT_SCOPE_TEMPLATE
    : KMZ_EXPORT_SCOPE_WAYLINES
  const truthTable = fnGetKmzActionExportTruthTable()
  fnRemoveDirectChildrenByLocalName(placemarkEl, 'actionGroup')

  const anchor = findFirstChildByTagSuffix(placemarkEl, 'waypointGimbalHeadingParam') || findFirstChildByTagSuffix(placemarkEl, 'isRisky') || null
  const normalizedGroups = fnSortActionGroupsById(groups)
  normalizedGroups.forEach((group) => {
    const normalizedGroup = normalizeKmzActionGroupEntry(group, 0)
    if (!normalizedGroup) return
    const groupEl = createWpmlElement(doc, 'actionGroup', wpmlNs)
    if (!groupEl) return
    fnAppendWpmlTextChild(groupEl, doc, wpmlNs, 'actionGroupId', normalizedGroup.actionGroupId)
    fnAppendWpmlTextChild(groupEl, doc, wpmlNs, 'actionGroupStartIndex', normalizedGroup.actionGroupStartIndex)
    fnAppendWpmlTextChild(groupEl, doc, wpmlNs, 'actionGroupEndIndex', normalizedGroup.actionGroupEndIndex)
    fnAppendWpmlTextChild(groupEl, doc, wpmlNs, 'actionGroupMode', normalizedGroup.actionGroupMode)

    const trigger = (normalizedGroup.actionTrigger && typeof normalizedGroup.actionTrigger === 'object') ? normalizedGroup.actionTrigger : {}
    const triggerEl = createWpmlElement(doc, 'actionTrigger', wpmlNs)
    if (triggerEl) {
      fnAppendWpmlTextChild(triggerEl, doc, wpmlNs, 'actionTriggerType', String(trigger.actionTriggerType || 'reachPoint'))
      Object.keys(trigger)
        .filter((key) => String(key || '').trim() && key !== 'actionTriggerType')
        .sort((a, b) => String(a).localeCompare(String(b)))
        .forEach((key) => {
          fnAppendWpmlTextChild(triggerEl, doc, wpmlNs, key, trigger[key])
        })
      groupEl.appendChild(triggerEl)
    }

    const actions = Array.isArray(normalizedGroup.actions) ? normalizedGroup.actions : []
    const filteredActions = actions
      .map((action) => fnFilterKmzActionByExportScope(action, scope, truthTable))
      .filter((item) => !!item)
    if (!filteredActions.length) return

    filteredActions.forEach((normalizedAction, actionIdx) => {
      const actionEl = createWpmlElement(doc, 'action', wpmlNs)
      if (!actionEl) return
      fnAppendWpmlTextChild(actionEl, doc, wpmlNs, 'actionId', actionIdx)
      fnAppendWpmlTextChild(actionEl, doc, wpmlNs, 'actionActuatorFunc', normalizedAction.actuator)
      const paramsEl = createWpmlElement(doc, 'actionActuatorFuncParam', wpmlNs)
      if (paramsEl) {
        const params = (normalizedAction.params && typeof normalizedAction.params === 'object') ? normalizedAction.params : {}
        Object.keys(params)
          .filter((key) => String(key || '').trim())
          .sort((a, b) => String(a).localeCompare(String(b)))
          .forEach((key) => {
            fnAppendWpmlTextChild(paramsEl, doc, wpmlNs, key, params[key])
          })
        actionEl.appendChild(paramsEl)
      }
      groupEl.appendChild(actionEl)
    })

    if (anchor && anchor.parentNode === placemarkEl) {
      placemarkEl.insertBefore(groupEl, anchor)
    } else {
      placemarkEl.appendChild(groupEl)
    }
  })
}

const fnApplyActionGroupsToExportPlacemarks = function (point, targetTemplatePm, targetWaylinesPm, fallbackIndex) {
  const templateGroups = fnGetNormalizedTemplateActionGroupsFromPoint(point, fallbackIndex)
  const waylinesGroups = fnResolveWaylinesActionGroupsForExport(point, fallbackIndex)
  fnWriteActionGroupsToPlacemark(targetTemplatePm, templateGroups, KMZ_EXPORT_SCOPE_TEMPLATE)
  fnWriteActionGroupsToPlacemark(targetWaylinesPm, waylinesGroups, KMZ_EXPORT_SCOPE_WAYLINES)
}

const fnParseNonNegativeInt = function (value) {
  const n = Number(String(value === null || value === undefined ? '' : value).trim())
  if (!Number.isFinite(n)) return null
  const normalized = Math.trunc(n)
  return normalized >= 0 ? normalized : null
}

const fnNormalizePointNameText = function (value) {
  if (Array.isArray(value)) return String(value[0] || '').trim()
  if (value && typeof value === 'object') {
    if (value._text !== undefined && value._text !== null) return String(value._text).trim()
    return ''
  }
  return String(value === null || value === undefined ? '' : value).trim()
}

const fnBuildFallbackFileSuffix = function (point, fallbackIndex) {
  const pointProps = (point && point.props && typeof point.props === 'object') ? point.props : {}
  const idFromWpml = fnParseNonNegativeInt(_.get(pointProps, 'wpml:index'))
  const idFromPlain = fnParseNonNegativeInt(_.get(pointProps, 'index'))
  const idFromIdx = fnParseNonNegativeInt(fallbackIndex)
  const safeId = idFromWpml !== null
    ? idFromWpml
    : (idFromPlain !== null ? idFromPlain : (idFromIdx !== null ? idFromIdx : 0))
  return KMZ_FILE_SUFFIX_FALLBACK_PREFIX + String(safeId).padStart(4, '0')
}

const fnNormalizeActionGroupsFileSuffixForExport = function (groups, point, fallbackIndex) {
  const pointName = fnNormalizePointNameText(getPointName(point))
  const fallbackSuffix = fnBuildFallbackFileSuffix(point, fallbackIndex)
  return (Array.isArray(groups) ? groups : []).map((group) => {
    const srcGroup = (group && typeof group === 'object') ? group : {}
    const srcActions = Array.isArray(srcGroup.actions) ? srcGroup.actions : []
    const nextActions = srcActions.map((action) => {
      const srcAction = (action && typeof action === 'object') ? action : {}
      const srcParams = (srcAction.params && typeof srcAction.params === 'object') ? srcAction.params : null
      if (!srcParams || !Object.prototype.hasOwnProperty.call(srcParams, 'fileSuffix')) return srcAction
      const currentSuffix = String(srcParams.fileSuffix === undefined || srcParams.fileSuffix === null ? '' : srcParams.fileSuffix).trim()
      let nextSuffix = currentSuffix
      if (currentSuffix === KMZ_FILE_SUFFIX_POINT_NAME_TOKEN) {
        nextSuffix = pointName || fallbackSuffix
      }
      if (!nextSuffix) nextSuffix = fallbackSuffix
      if (nextSuffix === currentSuffix) return srcAction
      return _.assign({}, srcAction, {
        params: _.assign({}, srcParams, { fileSuffix: nextSuffix })
      })
    })
    return _.assign({}, srcGroup, { actions: nextActions })
  })
}

const fnGetAllocatorState = function (actionGroupIdAllocator) {
  if (typeof actionGroupIdAllocator !== 'function') return null
  let state = kmzActionGroupAllocatorStateMap.get(actionGroupIdAllocator)
  if (state) return state
  state = {
    seen: new Set(),
    fallbackNext: 0
  }
  kmzActionGroupAllocatorStateMap.set(actionGroupIdAllocator, state)
  return state
}

const fnAllocateUniqueActionGroupId = function (actionGroupIdAllocator, state) {
  if (!state || typeof actionGroupIdAllocator !== 'function') return null
  for (let i = 0; i < 1024; i += 1) {
    const raw = actionGroupIdAllocator()
    const parsed = fnParseNonNegativeInt(raw)
    if (parsed === null) continue
    if (state.seen.has(parsed)) continue
    state.seen.add(parsed)
    return parsed
  }
  let fallback = Math.max(0, state.fallbackNext)
  while (state.seen.has(fallback)) fallback += 1
  state.fallbackNext = fallback + 1
  state.seen.add(fallback)
  return fallback
}

const normalizeKmzWaypointForExport = function (
  point,
  index,
  actionGroupIdAllocator,
  fnSetByPath,
  fnUpdatePointXmlByPath,
  fnGetKmzActionGroupsFromProps,
  fnSetPointKmzActionGroupsWithContext,
  fnToBoolLike
) {
  if (!point) return
  const idx = Number.isFinite(Number(index)) ? Number(index) : 0
  point.idx = idx
  if (!point.props || typeof point.props !== 'object') point.props = {}
  fnSetByPath(point.props, 'wpml:index', idx)
  fnUpdatePointXmlByPath(point, 'wpml:index', idx)
  fnSetByPath(point.props, 'index', idx)
  fnUpdatePointXmlByPath(point, 'index', idx)

  const templateHeadingState = fnNormalizeHeadingExportState(
    point,
    resolveKmzTemplateExportHeadingField,
    resolveKmzTemplateExportUseGlobalHeading,
    fnToBoolLike
  )
  const waylinesHeadingState = fnNormalizeHeadingExportState(
    point,
    resolveKmzWaylinesExportHeadingField,
    resolveKmzWaylinesExportUseGlobalHeading,
    fnToBoolLike
  )
  const templateNode = point.xmlEl || null
  const waylinesNode = Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
  const templateHeadingChanged = fnNormalizeKmzHeadingForExportNode(templateNode, templateHeadingState)
  const waylinesHeadingChanged = fnNormalizeKmzHeadingForExportNode(waylinesNode, waylinesHeadingState)
  const headingChanged = templateHeadingChanged || waylinesHeadingChanged
  if (headingChanged) {
    syncKmzPointHeadingScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateNode ? templateNode.parentElement : null,
      waylinesFolderEl: waylinesNode ? waylinesNode.parentElement : null
    })
  }

  const gimbalChanged = fnNormalizeKmzGimbalForExportNodes(templateNode, waylinesNode, point)
  if (gimbalChanged) {
    syncKmzPointGimbalScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateNode ? templateNode.parentElement : null,
      waylinesFolderEl: waylinesNode ? waylinesNode.parentElement : null
    })
  }

  const templateTurnChanged = fnNormalizeKmzTurnForExportNode(templateNode, point, KMZ_EXPORT_SCOPE_TEMPLATE)
  const waylinesTurnChanged = fnNormalizeKmzTurnForExportNode(waylinesNode, point, KMZ_EXPORT_SCOPE_WAYLINES)
  const turnChanged = templateTurnChanged || waylinesTurnChanged
  if (turnChanged) {
    syncKmzPointTurnScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateNode ? templateNode.parentElement : null,
      waylinesFolderEl: waylinesNode ? waylinesNode.parentElement : null
    })
  }

  const templateStraightLineChanged = fnNormalizeKmzStraightLineForExportNode(templateNode, point, KMZ_EXPORT_SCOPE_TEMPLATE)
  const waylinesStraightLineChanged = fnNormalizeKmzStraightLineForExportNode(waylinesNode, point, KMZ_EXPORT_SCOPE_WAYLINES)
  const straightLineChanged = templateStraightLineChanged || waylinesStraightLineChanged
  if (straightLineChanged) {
    syncKmzPointStraightLineScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateNode ? templateNode.parentElement : null,
      waylinesFolderEl: waylinesNode ? waylinesNode.parentElement : null
    })
  }

  if (typeof fnGetKmzActionGroupsFromProps !== 'function') return
  if (typeof fnSetPointKmzActionGroupsWithContext !== 'function') return
  const groups = fnGetKmzActionGroupsFromProps(point.props, point)
  if (!Array.isArray(groups) || !groups.length) return
  const groupsWithSafeFileSuffix = fnNormalizeActionGroupsFileSuffixForExport(groups, point, idx)

  const baseOptions = {
    pruneEmptyGroups: true,
    alignReachPointRangeToIndex: true,
    alignBetweenAdjacentPointsRangeToIndex: true,
    ensureMultipleTimingTriggerParam: true
  }
  const fnSetGroupsIfChanged = function (nextGroups) {
    if (!Array.isArray(nextGroups)) return
    if (_.isEqual(nextGroups, groups)) return
    fnSetPointKmzActionGroupsWithContext(point, nextGroups, baseOptions)
  }
  if (typeof actionGroupIdAllocator !== 'function') {
    fnSetGroupsIfChanged(groupsWithSafeFileSuffix)
    return
  }

  const state = fnGetAllocatorState(actionGroupIdAllocator)
  if (!state) {
    fnSetGroupsIfChanged(groupsWithSafeFileSuffix)
    return
  }
  fnReserveActionGroupIdsFromWaylinesMeta(point, state)

  const nextGroups = groupsWithSafeFileSuffix.map((group) => {
    const next = _.cloneDeep(group || {})
    const parsed = fnParseNonNegativeInt(next.actionGroupId)
    if (parsed !== null && !state.seen.has(parsed)) {
      state.seen.add(parsed)
      next.actionGroupId = String(parsed)
      return next
    }
    const allocated = fnAllocateUniqueActionGroupId(actionGroupIdAllocator, state)
    const assigned = allocated === null ? '0' : String(allocated)
    next.actionGroupId = assigned
    return next
  })

  fnSetGroupsIfChanged(nextGroups)
}

const normalizeKmzMissionLevelForExport = function (importMissionState) {
  if (!importMissionState || !Array.isArray(importMissionState.waypoints)) return false
  const altitudeCtx = fnBuildKmzAltitudeSyncContext(importMissionState)
  const sourceMissionAltContext = getMissionAltitudeContextFor3d(importMissionState, altitudeCtx)
  const firstPoint = importMissionState.waypoints.find((point) => !!point) || null
  const nextTemplateGlobalHeight = firstPoint
    ? toFiniteNumber(resolveKmzTemplateExportGlobalHeight(
        firstPoint,
        resolvePortalPresetDefaultValue('Folder.wpml:globalHeight')
      ))
    : null
  const sourceTemplateHeightMode = firstPoint
    ? resolveKmzTemplateExportHeightMode(firstPoint, sourceMissionAltContext.heightMode)
    : sourceMissionAltContext.heightMode
  const sourceExecuteHeightMode = firstPoint
    ? resolveKmzWaylinesExportExecuteHeightMode(firstPoint, sourceMissionAltContext.executeHeightMode)
    : sourceMissionAltContext.executeHeightMode
  const nextTemplateHeightMode = fnResolveTemplateHeightModeForExport(sourceTemplateHeightMode, altitudeCtx)
  const nextExecuteHeightMode = fnResolveExecuteHeightModeForExport(sourceExecuteHeightMode, nextTemplateHeightMode, altitudeCtx)
  let dirty = false
  if (firstPoint) {
    const nextTemplateTurnMode = resolveKmzTemplateExportGlobalTurnMode(
      firstPoint,
      resolvePortalPresetDefaultValue('Folder.wpml:globalWaypointTurnMode')
    )
    const nextTemplateGlobalUseStraightLine = resolveKmzTemplateExportGlobalUseStraightLine(
      firstPoint,
      resolvePortalPresetDefaultValue('Folder.wpml:globalUseStraightLine')
    )
    const nextWaylinesTurnMode = resolveKmzWaylinesExportGlobalTurnMode(
      firstPoint,
      resolvePortalPresetDefaultValue('Folder.wpml:globalWaypointTurnMode')
    )
    if (updateXmlByPath(importMissionState.folderEl, 'wpml:globalWaypointTurnMode', nextTemplateTurnMode, function () {})) {
      dirty = true
    }
    if (updateXmlByPath(importMissionState.folderEl, 'wpml:globalUseStraightLine', nextTemplateGlobalUseStraightLine, function () {})) {
      dirty = true
    }
    if (updateXmlByPath(importMissionState.secondaryFolderEl, 'wpml:globalWaypointTurnMode', nextWaylinesTurnMode, function () {})) {
      dirty = true
    }
    KMZ_PAYLOAD_CHILDREN.forEach((childName) => {
      const nextValue = resolveKmzTemplateExportPayloadField(
        firstPoint,
        childName,
        resolvePortalPresetDefaultValue('Folder.wpml:payloadParam.wpml:' + childName)
      )
      if (updateXmlByPath(importMissionState.folderEl, 'wpml:payloadParam.wpml:' + childName, nextValue, function () {})) {
        dirty = true
      }
    })
  }
  if (nextTemplateGlobalHeight !== null && updateXmlByPath(importMissionState.folderEl, 'wpml:globalHeight', nextTemplateGlobalHeight, function () {})) {
    dirty = true
  }
  if (fnSetXmlLeafBySuffixPath(importMissionState.folderEl, ['waylineCoordinateSysParam', 'heightMode'], nextTemplateHeightMode)) {
    dirty = true
  }
  if (fnSetXmlLeafBySuffixPath(importMissionState.secondaryFolderEl, ['executeHeightMode'], nextExecuteHeightMode)) {
    dirty = true
  }
  syncKmzMissionAltitudeScopesFromXml(importMissionState, xmlToObj)
  syncKmzMissionPayloadScopesFromXml(importMissionState, xmlToObj)
  const targetMissionAltContext = getMissionAltitudeContextFor3d(importMissionState, altitudeCtx)
  const syncResult = syncPointsAltitudeFields(importMissionState.waypoints, {
    sourceMissionAltContext: sourceMissionAltContext,
    targetMissionAltContext: targetMissionAltContext
  }, altitudeCtx)
  if (syncResult && syncResult.changedCount > 0) {
    dirty = true
  }
  return dirty
}

const fnParsePlacemarkCoordinates = function (placemarkEl) {
  return parsePlacemarkCoordinates(placemarkEl, parseKmlCoordTriplet)
}

const syncTemplateEllipsoidHeightPrecision = function (targetTemplatePm, sourceTemplatePm, sourceWaylinesPm) {
  if (!targetTemplatePm || !sourceWaylinesPm) return
  const targetEllipsoidEl = findFirstChildByTagSuffix(targetTemplatePm, 'ellipsoidHeight')
  if (!targetEllipsoidEl) return
  const sourceWaylinesExecuteEl = findFirstChildByTagSuffix(sourceWaylinesPm, 'executeHeight')
  if (!sourceWaylinesExecuteEl) return
  const waylinesRaw = String(sourceWaylinesExecuteEl.textContent || '').trim()
  if (!waylinesRaw) return

  const sourceTemplateEllipsoidEl = findFirstChildByTagSuffix(sourceTemplatePm, 'ellipsoidHeight')
  const templateRaw = String(((sourceTemplateEllipsoidEl || targetEllipsoidEl).textContent) || '').trim()
  const waylinesNum = Number(waylinesRaw)
  const templateNum = Number(templateRaw)
  if (Number.isFinite(waylinesNum) && Number.isFinite(templateNum) && Math.abs(waylinesNum - templateNum) > 1e-6) {
    return
  }

  targetEllipsoidEl.textContent = waylinesRaw
}

const buildKmzCanonicalExportDocs = function (importMissionState, templateXml, waylinesXml, fnUpdateXmlByPath) {
  if (!importMissionState || !importMissionState.xmlDoc || !importMissionState.secondaryXmlDoc) return null
  if (!templateXml || !waylinesXml) return null
  const parser = new DOMParser()
  const templateDoc = parser.parseFromString(templateXml, 'application/xml')
  const waylinesDoc = parser.parseFromString(waylinesXml, 'application/xml')

  const sourceTemplateDocumentEl = importMissionState.documentEl || (importMissionState.xmlDoc.getElementsByTagName('Document')[0] || importMissionState.xmlDoc.documentElement)
  const sourceTemplateFolderEl = importMissionState.folderEl || (sourceTemplateDocumentEl ? sourceTemplateDocumentEl.getElementsByTagName('Folder')[0] : null)
  const sourceWaylinesDocumentEl = importMissionState.secondaryDocumentEl || (importMissionState.secondaryXmlDoc.getElementsByTagName('Document')[0] || importMissionState.secondaryXmlDoc.documentElement)
  const sourceWaylinesFolderEl = importMissionState.secondaryFolderEl || (sourceWaylinesDocumentEl ? sourceWaylinesDocumentEl.getElementsByTagName('Folder')[0] : null)

  const targetTemplateDocumentEl = templateDoc.getElementsByTagName('Document')[0] || templateDoc.documentElement
  const targetTemplateFolderEl = targetTemplateDocumentEl ? targetTemplateDocumentEl.getElementsByTagName('Folder')[0] : null
  const targetWaylinesDocumentEl = waylinesDoc.getElementsByTagName('Document')[0] || waylinesDoc.documentElement
  const targetWaylinesFolderEl = targetWaylinesDocumentEl ? targetWaylinesDocumentEl.getElementsByTagName('Folder')[0] : null

  if (!sourceTemplateDocumentEl || !sourceWaylinesDocumentEl || !targetTemplateFolderEl || !targetWaylinesFolderEl) {
    return null
  }

  copyXmlLeafValuesBySkeleton(sourceTemplateDocumentEl, targetTemplateDocumentEl)
  copyXmlLeafValuesBySkeleton(sourceWaylinesDocumentEl, targetWaylinesDocumentEl)

  const waylinesCreateEls = getDirectChildrenByLocalName(targetWaylinesDocumentEl, 'createTime')
  const waylinesUpdateEls = getDirectChildrenByLocalName(targetWaylinesDocumentEl, 'updateTime')
  waylinesCreateEls.concat(waylinesUpdateEls).forEach((node) => {
    if (node.parentNode === targetWaylinesDocumentEl) targetWaylinesDocumentEl.removeChild(node)
  })

  const targetTemplatePayloadEl = findFirstChildByTagSuffix(targetTemplateFolderEl, 'payloadParam')
  const sourceTemplatePayloadEl = findFirstChildByTagSuffix(sourceTemplateFolderEl, 'payloadParam')
  if (targetTemplatePayloadEl && sourceTemplatePayloadEl) {
    copyXmlLeafValuesBySkeleton(sourceTemplatePayloadEl, targetTemplatePayloadEl)
  }

  const templatePlacemarkSkeleton = getDirectChildrenByLocalName(targetTemplateFolderEl, 'Placemark')[0] || null
  const waylinesPlacemarkSkeleton = getDirectChildrenByLocalName(targetWaylinesFolderEl, 'Placemark')[0] || null
  if (!templatePlacemarkSkeleton || !waylinesPlacemarkSkeleton) return null

  getDirectChildrenByLocalName(targetTemplateFolderEl, 'Placemark').forEach((node) => {
    if (node.parentNode === targetTemplateFolderEl) targetTemplateFolderEl.removeChild(node)
  })
  getDirectChildrenByLocalName(targetWaylinesFolderEl, 'Placemark').forEach((node) => {
    if (node.parentNode === targetWaylinesFolderEl) targetWaylinesFolderEl.removeChild(node)
  })

  const sourceTemplatePlacemarks = getDirectChildrenByLocalName(sourceTemplateFolderEl, 'Placemark')
  const sourceWaylinesPlacemarks = getDirectChildrenByLocalName(sourceWaylinesFolderEl, 'Placemark')
  const waypoints = Array.isArray(importMissionState.waypoints) ? importMissionState.waypoints : []
  const firstPoint = waypoints.find((point) => !!point) || null
  const exportAltitudeCtx = fnBuildKmzAltitudeSyncContext(importMissionState)

  if (firstPoint) {
    const nextTemplateGlobalHeight = toFiniteNumber(resolveKmzTemplateExportGlobalHeight(
      firstPoint,
      resolvePortalPresetDefaultValue('Folder.wpml:globalHeight')
    ))
    const nextTemplateHeightMode = fnResolveTemplateHeightModeForExport(
      resolveKmzTemplateExportHeightMode(
        firstPoint,
        resolvePortalPresetDefaultValue('Folder.wpml:waylineCoordinateSysParam.wpml:heightMode')
      ),
      exportAltitudeCtx
    )
    const nextWaylinesExecuteHeightMode = fnResolveExecuteHeightModeForExport(
      resolveKmzWaylinesExportExecuteHeightMode(
        firstPoint,
        resolvePortalPresetDefaultValue('Folder.wpml:executeHeightMode')
      ),
      nextTemplateHeightMode,
      exportAltitudeCtx
    )
    if (nextTemplateGlobalHeight !== null) {
      fnUpdateXmlByPath(
        targetTemplateFolderEl,
        'wpml:globalHeight',
        nextTemplateGlobalHeight
      )
    }
    fnUpdateXmlByPath(
      targetTemplateFolderEl,
      'wpml:waylineCoordinateSysParam.wpml:heightMode',
      nextTemplateHeightMode
    )
    fnUpdateXmlByPath(
      targetWaylinesFolderEl,
      'wpml:executeHeightMode',
      nextWaylinesExecuteHeightMode
    )
    fnUpdateXmlByPath(
      targetTemplateFolderEl,
      'wpml:globalUseStraightLine',
      resolveKmzTemplateExportGlobalUseStraightLine(
        firstPoint,
        resolvePortalPresetDefaultValue('Folder.wpml:globalUseStraightLine')
      )
    )
    KMZ_PAYLOAD_CHILDREN.forEach((childName) => {
      fnUpdateXmlByPath(
        targetTemplateFolderEl,
        'wpml:payloadParam.wpml:' + childName,
        resolveKmzTemplateExportPayloadField(
          firstPoint,
          childName,
          resolvePortalPresetDefaultValue('Folder.wpml:payloadParam.wpml:' + childName)
        )
      )
    })
  }

  waypoints.forEach((pt, index) => {
    const sourceTemplatePm = (pt && pt.xmlEl) || sourceTemplatePlacemarks[index] || null
    const sourceWaylinesPm = (pt && Array.isArray(pt.linkedXmlEls) && pt.linkedXmlEls[0]) || sourceWaylinesPlacemarks[index] || null
    const targetTemplatePm = sourceTemplatePm
      ? sourceTemplatePm.cloneNode(true)
      : templatePlacemarkSkeleton.cloneNode(true)
    const targetWaylinesPm = sourceWaylinesPm
      ? sourceWaylinesPm.cloneNode(true)
      : waylinesPlacemarkSkeleton.cloneNode(true)

    if (!sourceTemplatePm) {
      copyXmlLeafValuesBySkeleton(sourceTemplatePm, targetTemplatePm)
    }
    if (!sourceWaylinesPm) {
      copyXmlLeafValuesBySkeleton(sourceWaylinesPm, targetWaylinesPm)
    }

    let coord = fnParsePlacemarkCoordinates(sourceWaylinesPm) || fnParsePlacemarkCoordinates(sourceTemplatePm)
    const lat = Number(pt && pt.latlng && pt.latlng[0])
    const lng = Number(pt && pt.latlng && pt.latlng[1])
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      coord = coord || { lat: lat, lng: lng, alt: null }
      coord.lat = lat
      coord.lng = lng
    }
    if (coord) {
      setPlacemarkCoordinates(targetTemplatePm, coord)
      setPlacemarkCoordinates(targetWaylinesPm, coord)
    }

    fnUpdateXmlByPath(targetTemplatePm, 'wpml:index', index)
    fnUpdateXmlByPath(targetWaylinesPm, 'wpml:index', index)
    syncTemplateEllipsoidHeightPrecision(targetTemplatePm, sourceTemplatePm, sourceWaylinesPm)
    fnEnsureTemplateWaypointHeadingParam(targetTemplatePm, sourceTemplatePm, sourceWaylinesPm, pt)
    fnEnsureTemplateWaypointTurnParam(targetTemplatePm, sourceTemplatePm, sourceWaylinesPm, pt)
    fnUpdateXmlByPath(
      targetTemplatePm,
      'wpml:useGlobalHeight',
      resolveKmzTemplateExportUseGlobalHeight(
        pt,
        resolvePortalPresetDefaultValue('wpml:useGlobalHeight')
      )
    )
    fnUpdateXmlByPath(
      targetWaylinesPm,
      'wpml:useGlobalHeight',
      resolveKmzWaylinesExportUseGlobalHeight(
        pt,
        resolvePortalPresetDefaultValue('wpml:useGlobalHeight')
      )
    )
    fnEnsurePlacemarkUseStraightLine(targetTemplatePm, pt, KMZ_EXPORT_SCOPE_TEMPLATE)
    fnEnsurePlacemarkUseStraightLine(targetWaylinesPm, pt, KMZ_EXPORT_SCOPE_WAYLINES)
    fnEnsureExportAltitudeFields(targetTemplatePm, targetWaylinesPm, pt)
    fnApplyActionGroupsToExportPlacemarks(pt, targetTemplatePm, targetWaylinesPm, index)

    targetTemplateFolderEl.appendChild(targetTemplatePm)
    targetWaylinesFolderEl.appendChild(targetWaylinesPm)
  })

  const payloadEl = findFirstChildByTagSuffix(targetTemplateFolderEl, 'payloadParam')
  const templatePlacemarksNow = getDirectChildrenByLocalName(targetTemplateFolderEl, 'Placemark')
  const lastTemplatePlacemark = templatePlacemarksNow.length ? templatePlacemarksNow[templatePlacemarksNow.length - 1] : null
  if (payloadEl && payloadEl.parentNode === targetTemplateFolderEl && lastTemplatePlacemark) {
    if (lastTemplatePlacemark.nextSibling) {
      targetTemplateFolderEl.insertBefore(payloadEl, lastTemplatePlacemark.nextSibling)
    } else {
      targetTemplateFolderEl.appendChild(payloadEl)
    }
  }

  return {
    templateDoc: templateDoc,
    waylinesDoc: waylinesDoc
  }
}

const parseKmzMission = function (templateXmlText, waylinesXmlText, fnXmlToObj) {
  const fnXmlToObjSafe = (typeof fnXmlToObj === 'function') ? fnXmlToObj : xmlToObj
  const parser = new DOMParser()
  const templateXml = parser.parseFromString(templateXmlText, 'application/xml')
  const waylinesXml = parser.parseFromString(waylinesXmlText, 'application/xml')
  const serializer = new XMLSerializer()

  const templateDocumentEl = templateXml.getElementsByTagName('Document')[0] || templateXml.documentElement
  const templateFolderEl = templateDocumentEl.getElementsByTagName('Folder')[0] || null
  const waylinesDocumentEl = waylinesXml.getElementsByTagName('Document')[0] || waylinesXml.documentElement
  const waylinesFolderEl = waylinesDocumentEl.getElementsByTagName('Folder')[0] || null
  const templatePlacemarks = templateFolderEl ? Array.from(templateFolderEl.getElementsByTagName('Placemark')) : []
  const waylinesPlacemarks = waylinesFolderEl ? Array.from(waylinesFolderEl.getElementsByTagName('Placemark')) : []
  const hasAnyTemplateActionGroups = templatePlacemarks.some((placemarkEl) => {
    return getDirectChildrenByLocalName(placemarkEl, 'actionGroup').length > 0
  })
  const waypoints = []
  const lineCoords = []
  const templateFolderProps = templateFolderEl ? fnXmlToObjSafe(templateFolderEl) : {}
  const waylinesFolderProps = waylinesFolderEl ? fnXmlToObjSafe(waylinesFolderEl) : {}

  const fnMergeWaypointPropsFromWayline = function (templateProps, waylineProps) {
    const merged = _.cloneDeep((templateProps && typeof templateProps === 'object') ? templateProps : {})
    const source = (waylineProps && typeof waylineProps === 'object') ? waylineProps : {}
    const executionPaths = [
      'wpml:index',
      'wpml:executeHeight',
      'wpml:waypointGimbalHeadingParam',
      'wpml:isRisky',
      'wpml:waypointWorkType'
    ]
    executionPaths.forEach((path) => {
      const value = _.get(source, path)
      if (value === undefined) return
      _.set(merged, path, _.cloneDeep(value))
    })
    return merged
  }

  waylinesPlacemarks.forEach((waylinesPm, index) => {
    const pointEl = waylinesPm.getElementsByTagName('Point')[0]
    if (!pointEl) return
    const coordEl = pointEl.getElementsByTagName('coordinates')[0]
    if (!coordEl) return
    const parsedCoord = parseKmlCoordTriplet(coordEl.textContent)
    if (!parsedCoord) return
    const templatePm = templatePlacemarks[index] || waylinesPm
    const templateProps = fnXmlToObjSafe(templatePm)
    const waylineProps = fnXmlToObjSafe(waylinesPm)
    const props = fnMergeWaypointPropsFromWayline(templateProps, waylineProps)
    applyKmzScopedHeadingProps(props, buildKmzScopedHeadingPropsFromSources({
      templatePointProps: templateProps,
      waylinePointProps: waylineProps,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    }))
    applyKmzScopedSpeedProps(props, buildKmzScopedSpeedPropsFromSources({
      templatePointProps: templateProps,
      waylinePointProps: waylineProps,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    }))
    applyKmzScopedGimbalProps(props, buildKmzScopedGimbalPropsFromSources({
      templatePointProps: templateProps,
      waylinePointProps: waylineProps,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    }))
    applyKmzScopedPayloadProps(props, buildKmzScopedPayloadPropsFromSources({
      templatePointProps: templateProps,
      waylinePointProps: waylineProps,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    }))
    applyKmzScopedTurnProps(props, buildKmzScopedTurnPropsFromSources({
      templatePointProps: templateProps,
      waylinePointProps: waylineProps,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    }))
    applyKmzScopedStraightLineProps(props, buildKmzScopedStraightLinePropsFromSources({
      templatePointProps: templateProps,
      waylinePointProps: waylineProps,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    }))
    applyKmzScopedAltitudeProps(props, buildKmzScopedAltitudePropsFromSources({
      templatePointProps: templateProps,
      waylinePointProps: waylineProps,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    }))
    const templateGroups = fnNormalizeKmzActionGroups(_.get(templateProps, 'wpml:actionGroup'), index)
    const waylineGroups = fnNormalizeKmzActionGroups(_.get(waylineProps, 'wpml:actionGroup'), index)
    const layered = fnBuildImportActionLayering(templateGroups, waylineGroups, {
      promoteWaylineIfTemplateEmpty: !hasAnyTemplateActionGroups
    })
    if (layered.templateActionGroups.length) {
      props['wpml:actionGroup'] = fnSerializeActionGroupsForProps(layered.templateActionGroups)
      props[KMZ_INTERNAL_TEMPLATE_ACTION_GROUPS_BASELINE_KEY] = _.cloneDeep(layered.templateActionGroupsBaseline)
    } else {
      delete props['wpml:actionGroup']
      delete props[KMZ_INTERNAL_TEMPLATE_ACTION_GROUPS_BASELINE_KEY]
    }
    if (layered.waylinesExecutionExtras.length) {
      props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_EXTRAS_KEY] = _.cloneDeep(layered.waylinesExecutionExtras)
    } else {
      delete props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_EXTRAS_KEY]
    }
    if (Object.keys(layered.waylinesExecutionOverrides || {}).length) {
      props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_OVERRIDES_KEY] = _.cloneDeep(layered.waylinesExecutionOverrides)
    } else {
      delete props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_OVERRIDES_KEY]
    }
    props.__rawXml = serializer.serializeToString(templatePm)
    props.coordinates = { lat: parsedCoord.lat, lng: parsedCoord.lng }
    if (parsedCoord.alt !== null) {
      props.coordinates.alt = parsedCoord.alt
    }
    waypoints.push({
      idx: index,
      latlng: [parsedCoord.lat, parsedCoord.lng],
      altitude: parsedCoord.alt,
      props: props,
      xmlEl: templatePm,
      linkedXmlEls: [waylinesPm]
    })
    lineCoords.push([parsedCoord.lat, parsedCoord.lng])
  })

  return {
    xmlDoc: templateXml,
    documentEl: templateDocumentEl,
    folderEl: templateFolderEl,
    importPlacemarkEl: null,
    importCoordEl: null,
    secondaryXmlDoc: waylinesXml,
    secondaryDocumentEl: waylinesDocumentEl,
    secondaryFolderEl: waylinesFolderEl,
    waypoints: waypoints,
    lineCoords: lineCoords,
    polygons: []
  }
}

const yieldKmzParseProgress = async function () {
  await new Promise(function (resolve) {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        resolve()
      })
      return
    }
    setTimeout(resolve, 0)
  })
}

const parseKmzMissionWithProgress = async function (templateXmlText, waylinesXmlText, fnXmlToObj, options) {
  const opts = (options && typeof options === 'object') ? options : {}
  const onProgress = (typeof opts.onProgress === 'function') ? opts.onProgress : function () {}
  const fnXmlToObjSafe = (typeof fnXmlToObj === 'function') ? fnXmlToObj : xmlToObj
  const parser = new DOMParser()
  const templateXml = parser.parseFromString(templateXmlText, 'application/xml')
  const waylinesXml = parser.parseFromString(waylinesXmlText, 'application/xml')
  const serializer = new XMLSerializer()

  const templateDocumentEl = templateXml.getElementsByTagName('Document')[0] || templateXml.documentElement
  const templateFolderEl = templateDocumentEl.getElementsByTagName('Folder')[0] || null
  const waylinesDocumentEl = waylinesXml.getElementsByTagName('Document')[0] || waylinesXml.documentElement
  const waylinesFolderEl = waylinesDocumentEl.getElementsByTagName('Folder')[0] || null
  const templatePlacemarks = templateFolderEl ? Array.from(templateFolderEl.getElementsByTagName('Placemark')) : []
  const waylinesPlacemarks = waylinesFolderEl ? Array.from(waylinesFolderEl.getElementsByTagName('Placemark')) : []
  const hasAnyTemplateActionGroups = templatePlacemarks.some((placemarkEl) => {
    return getDirectChildrenByLocalName(placemarkEl, 'actionGroup').length > 0
  })
  const waypoints = []
  const lineCoords = []
  const templateFolderProps = templateFolderEl ? fnXmlToObjSafe(templateFolderEl) : {}
  const waylinesFolderProps = waylinesFolderEl ? fnXmlToObjSafe(waylinesFolderEl) : {}
  const totalWaylinesPlacemarks = waylinesPlacemarks.length
  const waypointChunkSize = Math.max(10, Math.min(50, Math.ceil(totalWaylinesPlacemarks / 40) || 10))

  const fnMergeWaypointPropsFromWayline = function (templateProps, waylineProps) {
    const merged = _.cloneDeep((templateProps && typeof templateProps === 'object') ? templateProps : {})
    const source = (waylineProps && typeof waylineProps === 'object') ? waylineProps : {}
    const executionPaths = [
      'wpml:index',
      'wpml:executeHeight',
      'wpml:waypointGimbalHeadingParam',
      'wpml:isRisky',
      'wpml:waypointWorkType'
    ]
    executionPaths.forEach((path) => {
      const value = _.get(source, path)
      if (value === undefined) return
      _.set(merged, path, _.cloneDeep(value))
    })
    return merged
  }

  onProgress({
    phase: 'parse-kmz-waypoints',
    current: 0,
    total: totalWaylinesPlacemarks,
    percent: totalWaylinesPlacemarks > 0 ? 0 : 100
  })

  for (let index = 0; index < waylinesPlacemarks.length; index += 1) {
    const waylinesPm = waylinesPlacemarks[index]
    const pointEl = waylinesPm.getElementsByTagName('Point')[0]
    if (pointEl) {
      const coordEl = pointEl.getElementsByTagName('coordinates')[0]
      if (coordEl) {
        const parsedCoord = parseKmlCoordTriplet(coordEl.textContent)
        if (parsedCoord) {
          const templatePm = templatePlacemarks[index] || waylinesPm
          const templateProps = fnXmlToObjSafe(templatePm)
          const waylineProps = fnXmlToObjSafe(waylinesPm)
          const props = fnMergeWaypointPropsFromWayline(templateProps, waylineProps)
          applyKmzScopedHeadingProps(props, buildKmzScopedHeadingPropsFromSources({
            templatePointProps: templateProps,
            waylinePointProps: waylineProps,
            templateFolderProps: templateFolderProps,
            waylinesFolderProps: waylinesFolderProps
          }))
          applyKmzScopedSpeedProps(props, buildKmzScopedSpeedPropsFromSources({
            templatePointProps: templateProps,
            waylinePointProps: waylineProps,
            templateFolderProps: templateFolderProps,
            waylinesFolderProps: waylinesFolderProps
          }))
          applyKmzScopedGimbalProps(props, buildKmzScopedGimbalPropsFromSources({
            templatePointProps: templateProps,
            waylinePointProps: waylineProps,
            templateFolderProps: templateFolderProps,
            waylinesFolderProps: waylinesFolderProps
          }))
          applyKmzScopedPayloadProps(props, buildKmzScopedPayloadPropsFromSources({
            templatePointProps: templateProps,
            waylinePointProps: waylineProps,
            templateFolderProps: templateFolderProps,
            waylinesFolderProps: waylinesFolderProps
          }))
          applyKmzScopedTurnProps(props, buildKmzScopedTurnPropsFromSources({
            templatePointProps: templateProps,
            waylinePointProps: waylineProps,
            templateFolderProps: templateFolderProps,
            waylinesFolderProps: waylinesFolderProps
          }))
          applyKmzScopedStraightLineProps(props, buildKmzScopedStraightLinePropsFromSources({
            templatePointProps: templateProps,
            waylinePointProps: waylineProps,
            templateFolderProps: templateFolderProps,
            waylinesFolderProps: waylinesFolderProps
          }))
          applyKmzScopedAltitudeProps(props, buildKmzScopedAltitudePropsFromSources({
            templatePointProps: templateProps,
            waylinePointProps: waylineProps,
            templateFolderProps: templateFolderProps,
            waylinesFolderProps: waylinesFolderProps
          }))
          const templateGroups = fnNormalizeKmzActionGroups(_.get(templateProps, 'wpml:actionGroup'), index)
          const waylineGroups = fnNormalizeKmzActionGroups(_.get(waylineProps, 'wpml:actionGroup'), index)
          const layered = fnBuildImportActionLayering(templateGroups, waylineGroups, {
            promoteWaylineIfTemplateEmpty: !hasAnyTemplateActionGroups
          })
          if (layered.templateActionGroups.length) {
            props['wpml:actionGroup'] = fnSerializeActionGroupsForProps(layered.templateActionGroups)
            props[KMZ_INTERNAL_TEMPLATE_ACTION_GROUPS_BASELINE_KEY] = _.cloneDeep(layered.templateActionGroupsBaseline)
          } else {
            delete props['wpml:actionGroup']
            delete props[KMZ_INTERNAL_TEMPLATE_ACTION_GROUPS_BASELINE_KEY]
          }
          if (layered.waylinesExecutionExtras.length) {
            props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_EXTRAS_KEY] = _.cloneDeep(layered.waylinesExecutionExtras)
          } else {
            delete props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_EXTRAS_KEY]
          }
          if (Object.keys(layered.waylinesExecutionOverrides || {}).length) {
            props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_OVERRIDES_KEY] = _.cloneDeep(layered.waylinesExecutionOverrides)
          } else {
            delete props[KMZ_INTERNAL_WAYLINES_ACTION_GROUP_OVERRIDES_KEY]
          }
          props.__rawXml = serializer.serializeToString(templatePm)
          props.coordinates = { lat: parsedCoord.lat, lng: parsedCoord.lng }
          if (parsedCoord.alt !== null) {
            props.coordinates.alt = parsedCoord.alt
          }
          waypoints.push({
            idx: index,
            latlng: [parsedCoord.lat, parsedCoord.lng],
            altitude: parsedCoord.alt,
            props: props,
            xmlEl: templatePm,
            linkedXmlEls: [waylinesPm]
          })
          lineCoords.push([parsedCoord.lat, parsedCoord.lng])
        }
      }
    }
    const processed = index + 1
    if ((processed % waypointChunkSize) === 0 || processed === totalWaylinesPlacemarks) {
      onProgress({
        phase: 'parse-kmz-waypoints',
        current: processed,
        total: totalWaylinesPlacemarks,
        percent: totalWaylinesPlacemarks > 0 ? Math.round((processed / totalWaylinesPlacemarks) * 100) : 100
      })
      if (processed < totalWaylinesPlacemarks) {
        await yieldKmzParseProgress()
      }
    }
  }

  return {
    xmlDoc: templateXml,
    documentEl: templateDocumentEl,
    folderEl: templateFolderEl,
    importPlacemarkEl: null,
    importCoordEl: null,
    secondaryXmlDoc: waylinesXml,
    secondaryDocumentEl: waylinesDocumentEl,
    secondaryFolderEl: waylinesFolderEl,
    waypoints: waypoints,
    lineCoords: lineCoords,
    polygons: []
  }
}

export {
  normalizeKmzWaypointForExport,
  normalizeKmzMissionLevelForExport,
  buildKmzCanonicalExportDocs,
  parseKmzMission,
  parseKmzMissionWithProgress
}
